import { LOGOUT } from '@comeon/actions-public';
import reducerRegistry from '@comeon/reducer-registry';
import { EXCLUSIVE_GAME_WIDGET_UPDATE_SUBSCRIPTIONS } from '~/components/casino-shared/exclusive-game-widget/notification/ducks';
import { SET_GRAPHYTE_LIVE_CASINO_EXPLORE_RECOMMENDATIONS } from '~/components/search/ducks';
import { requestApi } from '~/services/api.js';
import mergeObjects from '~/utils/merge-objects';
import enhanceLiveData from '../utils/enhance-live-data';

const LIVE_CASINO_FEED_SOCKET_RETRY_TIME = 5000;
const ACTION_PREFIX = `@komigen/mobile-frontend/live-casino`;
export const SET_LIVE_CASINO_DATA = `${ACTION_PREFIX}/SET_LIVE_CASINO_DATA`;

export const SET_CAROUSEL_ITEMS = `${ACTION_PREFIX}/SET_CAROUSEL_ITEMS`;

export const SET_BANNER_AUTH = `${ACTION_PREFIX}/SET_BANNER_AUTH`;

export const FETCH_CASINO_ALL_GAMES_START = `${ACTION_PREFIX}/FETCH_CASINO_ALL_GAMES_START`;

export const HYDRATE_LIVE_CASINO_GAMES = `${ACTION_PREFIX}/HYDRATE_LIVE_CASINO_GAMES`;

export const INVALIDATE_LIVE_CASINO_DATA = `${ACTION_PREFIX}/INVALIDATE_LIVE_CASINO_DATA`;

export const LIVE_CASINO_FEED_SOCKET_ON_CONNECTION_OPEN = `${ACTION_PREFIX}/LIVE_CASINO_FEED_SOCKET_ON_CONNECTION_OPEN`;
export const LIVE_CASINO_FEED_SOCKET_SET_RETRY_TIMEOUT = `${ACTION_PREFIX}/LIVE_CASINO_FEED_SOCKET_SET_RETRY_TIMEOUT`;
export const LIVE_CASINO_FEED_SOCKET_ON_MESSAGE = `${ACTION_PREFIX}/LIVE_CASINO_FEED_SOCKET_ON_MESSAGE`;
export const LIVE_CASINO_FEED_SOCKET_CLOSE_CONNECTION = `${ACTION_PREFIX}/LIVE_CASINO_FEED_SOCKET_CLOSE_CONNECTION`;

const REDUCER_NAME = 'liveCasino';
const host = '/';

export const EXPLORE_LIVE_CASINO_SUCCESS = 'live_success';
export const EXPLORE_LIVE_CASINO_LOADING = 'live_loading';
export const EXPLORE_LIVE_CASINO_ERROR = 'live_error';

// incoming game has these below fields without a status wrapper field
const convertIncomingGame = ({
	activePlayers,
	activeSeats,
	isOnline,
	totalSeats,
	betLimits,
	inGameImage,
	rouletteNumbers,
	...rest
}) => {
	// betLimits are received as an array of objects, instead it should have been an object, thus converting it
	const convertedBetLimits = betLimits ? betLimits[0] : null;

	return {
		...rest,
		...(activePlayers !== undefined ||
		activeSeats ||
		isOnline !== undefined ||
		totalSeats !== undefined ||
		rouletteNumbers !== undefined
			? {
					status: {
						...(activePlayers !== undefined ? { activePlayers } : null),
						...(activeSeats !== undefined ? { activeSeats } : null),
						...(isOnline !== undefined ? { isOnline } : null),
						...(totalSeats !== undefined ? { totalSeats } : null),
						...(inGameImage ? { inGameImage } : null),
						...(rouletteNumbers ? { rouletteNumbers } : null)
					}
			  }
			: null),
		...(convertedBetLimits ? { betLimits: convertedBetLimits } : null)
	};
};

const invalidateLiveCasinoData = () => ({ type: INVALIDATE_LIVE_CASINO_DATA });

const fetchLiveCasinoData = (currency, forceRefresh = false) => {
	return async (dispatch, getState) => {
		const state = getState();

		if (!forceRefresh && state.liveCasino.liveCasinoData?.length) return;

		// FIXME
		if (currency === '$') {
			currency = 'USD'; // eslint-disable-line no-param-reassign
		}

		try {
			const response = await requestApi(
				`${host}live-feed-service/state/v1?currency=${currency}&franchiseId=${state.brand.franchiseId}`,
				{},
				'GET',
				false,
				false,
				{},
				{ cache: 'default' }
			);

			if (response.error) {
				dispatch({
					type: SET_LIVE_CASINO_DATA,
					payload: []
				});
				return;
			}

			dispatch({
				type: SET_LIVE_CASINO_DATA,
				payload: response
			});
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);

			dispatch({
				type: SET_LIVE_CASINO_DATA,
				payload: []
			});
		}
	};
};

const createRetryTimeout = (
	dispatch,
	getState,
	retryFn,
	retryTime = LIVE_CASINO_FEED_SOCKET_RETRY_TIME,
	timeoutType
) => {
	if (!getState()[REDUCER_NAME][timeoutType]) {
		const retryTimeout = setTimeout(retryFn, retryTime);

		dispatch({
			type: LIVE_CASINO_FEED_SOCKET_SET_RETRY_TIMEOUT,
			payload: { retryTimeout, timeoutType }
		});
	}
};

const establishLiveCasinoFeedSocketConnection = ({ currency, franchiseId }) => {
	return async (dispatch, getState) => {
		const socketUrl = `wss://${window.location.host}/livecasino/ws?currency=${currency}&franchiseId=${franchiseId}`;
		const conn = new WebSocket(socketUrl);
		const timeoutType = 'LiveCasinoFeedSocketTimeout';

		conn.addEventListener('error', () => {
			if (window.LiveCasinoFeedSocket) {
				// remove socket instance when connection is closed
				window.LiveCasinoFeedSocket = null;
			}

			// retry in LIVE_CASINO_FEED_SOCKET_RETRY_TIME
			createRetryTimeout(
				dispatch,
				getState,
				() => {
					dispatch(establishLiveCasinoFeedSocketConnection({ currency, franchiseId }));
					dispatch({
						type: LIVE_CASINO_FEED_SOCKET_SET_RETRY_TIMEOUT,
						payload: { retryTimeout: null, timeoutType }
					});
				},
				LIVE_CASINO_FEED_SOCKET_RETRY_TIME,
				timeoutType
			);
		});

		conn.addEventListener('open', async () => {
			await dispatch({
				type: LIVE_CASINO_FEED_SOCKET_ON_CONNECTION_OPEN
			});

			window[timeoutType] = conn;
		});

		conn.addEventListener('message', messageEvent => {
			try {
				const data = JSON.parse(messageEvent?.data);

				if (data.isGlobalStateRefreshed) {
					// a full data refresh is required from REST endpoint
					return dispatch(fetchLiveCasinoData(currency, true));
				}

				dispatch({
					type: LIVE_CASINO_FEED_SOCKET_ON_MESSAGE,
					payload: data
				});
			} catch (e) {
				console.warn(e); // eslint-disable-line
			}
		});

		conn.addEventListener('close', closeEvent => {
			if (window.LiveCasinoFeedSocket) {
				// remove live feed socket when connection is closed
				window.LiveCasinoFeedSocket = null;
			}

			if (closeEvent.code === 1000) {
				// no action
			} else {
				// retry
				createRetryTimeout(
					dispatch,
					getState,
					() => {
						dispatch(establishLiveCasinoFeedSocketConnection({ currency, franchiseId }));
						dispatch({
							type: LIVE_CASINO_FEED_SOCKET_SET_RETRY_TIMEOUT,
							payload: { retryTimeout: null, timeoutType }
						});
					},
					LIVE_CASINO_FEED_SOCKET_RETRY_TIME,
					timeoutType
				);
			}
		});
	};
};

const closeLiveCasinoFeedSocket = () => ({
	type: LIVE_CASINO_FEED_SOCKET_CLOSE_CONNECTION
});

const fetchLiveCasinoGames = () => {
	return async (
		dispatch,
		getState,
		{
			isPictureElementEnabled,
			imageDescriptors,
			imageFormats,
			campaignBackgroundImageDescriptors,
			campaignForegroundImageDescriptors
		}
	) => {
		const state = getState();

		const {
			authentication: { authenticated },
			liveCasino: { exploreLiveCasinoStatus, wasAuthenticated }
		} = state;

		if (
			exploreLiveCasinoStatus === EXPLORE_LIVE_CASINO_LOADING ||
			(exploreLiveCasinoStatus === EXPLORE_LIVE_CASINO_SUCCESS && authenticated === wasAuthenticated)
		) {
			return;
		}

		dispatch({
			type: FETCH_CASINO_ALL_GAMES_START
		});

		const data = await enhanceLiveData(
			{
				isPictureElementEnabled,
				imageDescriptors,
				imageFormats,
				campaignBackgroundImageDescriptors,
				campaignForegroundImageDescriptors
			},
			state.liveCasino
		);

		await dispatch({
			type: HYDRATE_LIVE_CASINO_GAMES,
			payload: {
				...data,
				hydrated: true,
				loading: false,
				wasAuthenticated: authenticated,
				exploreLiveCasinoStatus: EXPLORE_LIVE_CASINO_SUCCESS
			}
		});

		const { exploreCategories, games } = data;
		// categories are not searchable by their types so accepting the first simple category on live-casino
		const recommendedCategory = exploreCategories.find(category => category.type === 'simple');
		const recommendedGames = recommendedCategory?.gameCodes.map(gameCode => games[gameCode]);

		if (recommendedGames) {
			// fills the smart search results with an initial set of recommended games
			dispatch({
				type: SET_GRAPHYTE_LIVE_CASINO_EXPLORE_RECOMMENDATIONS,
				payload: recommendedGames
			});
		}
	};
};

const setCarouselItems = items => ({
	type: SET_CAROUSEL_ITEMS,
	payload: { items }
});

const setBannerAuth = auth => ({
	type: SET_BANNER_AUTH,
	payload: auth
});

const fetchCarouselItems = (defaultItem, itemCount) => {
	return async (dispatch, getState) => {
		const {
			authentication: { authenticated },
			liveCasino: { bannerAuth, carouselItems }
		} = getState();

		if (!carouselItems || bannerAuth !== authenticated) {
			let newCarouselItems = [defaultItem];
			const response = await requestApi(
				`/rest/state/promotions/?categoryType=live-casino&type=campaign&omitPromotionsPageCheck=true${`&campaignCount=${itemCount}`}`
			);
			if (response.status === 'SUCCESS' && response?.result?.length > 0) {
				newCarouselItems = [...response.result];
				newCarouselItems.sort((a, b) => {
					return a.sortOrder - b.sortOrder;
				});
			}

			dispatch(setBannerAuth(authenticated));
			dispatch(setCarouselItems(newCarouselItems));
		}
	};
};

const initialState = {
	loading: false,
	hydrated: false,
	wasAuthenticated: false,
	timeout: null,
	liveCasinoData: [],
	carouselItems: null,
	bannerAuth: false,
	games: {},
	gameNamesByIdx: [],
	gameCodesByProviderCode: {},
	gameCodesBySubProviderCode: {},
	gameCodesByIdx: [],
	gameCodesByPublishCode: {},
	campaigns: [],
	exploreCategories: [],
	exploreLiveCasinoStatus: '',
	providers: {},
	subProviders: {}
};

// eslint-disable-next-line default-param-last
const reducer = (stateTemp = initialState, { type, payload }) => {
	const state = stateTemp.initialized ? stateTemp : { ...initialState, ...stateTemp, initialized: true };
	switch (type) {
		case LOGOUT:
			return {
				...state,
				exploreLiveCasinoStatus: ''
			};
		case SET_CAROUSEL_ITEMS:
			return {
				...state,
				carouselItems: payload.items
			};
		case SET_BANNER_AUTH:
			return {
				...state,
				bannerAuth: payload
			};
		case INVALIDATE_LIVE_CASINO_DATA:
			return {
				...state,
				hydrated: false
			};
		case FETCH_CASINO_ALL_GAMES_START:
			return {
				...state,
				hydrated: false,
				loading: true,
				gameNamesByIdx: [],
				gameCodesByIdx: [],
				gameCodesByProviderCode: {},
				gameCodesBySubProviderCode: {}
			};
		case HYDRATE_LIVE_CASINO_GAMES: {
			const { games, ...rest } = payload;
			return {
				...state,
				...rest,
				games: {
					...state.games,
					...games
				}
			};
		}
		case SET_LIVE_CASINO_DATA: {
			return {
				...state,
				liveCasinoData: payload
			};
		}
		case LIVE_CASINO_FEED_SOCKET_ON_MESSAGE: {
			// convert all the incoming games into a unified format that fits the rest data (missing status wrapper in messages)
			const providerGameStatesMap = Object.entries(payload.providerGameStatesMap || {}).reduce(
				(prev, [provider, games]) => {
					const convertedGamesArray = Object.entries(games).map(([gameCode, game]) => [
						gameCode,
						convertIncomingGame(game)
					]);

					return { ...prev, [provider]: Object.fromEntries(convertedGamesArray) };
				},
				{}
			);

			const updatedProviderGameStatesArray = state.liveCasinoData.map(({ provider, gameStates }) => {
				if (provider in providerGameStatesMap) {
					const newGameStatesForProvider = providerGameStatesMap[provider];

					return { provider, gameStates: mergeObjects(gameStates, newGameStatesForProvider) };
				}

				return { provider, gameStates };
			});

			// grab the providers that are missing in the current state
			const nonExistentProviders = Object.keys(providerGameStatesMap).filter(
				provider =>
					!state.liveCasinoData.some(({ provider: existingProvider }) => existingProvider === provider)
			);

			const finalData = updatedProviderGameStatesArray.concat(
				nonExistentProviders.map(provider => ({
					provider,
					gameStates: providerGameStatesMap[provider]
				}))
			);

			return {
				...state,
				liveCasinoData: finalData
			};
		}
		case LIVE_CASINO_FEED_SOCKET_SET_RETRY_TIMEOUT:
			return {
				...state,
				[payload.timeoutType]: payload.retryTimeout
			};
		case LIVE_CASINO_FEED_SOCKET_ON_CONNECTION_OPEN: {
			if (window.LiveCasinoFeedSocket) {
				window.LiveCasinoFeedSocket.close();
			}

			return state;
		}
		case LIVE_CASINO_FEED_SOCKET_CLOSE_CONNECTION: {
			if (window.LiveCasinoFeedSocket) {
				window.LiveCasinoFeedSocket.close(1000);
			}
			window.LiveCasinoFeedSocket = null;
			return state;
		}
		case EXCLUSIVE_GAME_WIDGET_UPDATE_SUBSCRIPTIONS: {
			return {
				...state,
				exploreCategories: state.exploreCategories.map(category => {
					if (category.type !== 'exclusive_game_widget' || !payload || payload.length === 0) {
						return category;
					}
					return {
						...category,
						gameLaunch: payload.find(updatedWidget => updatedWidget.widgetId === category.widgetId)
							.gameLaunch
					};
				})
			};
		}
		default:
			return state;
	}
};

reducerRegistry.register(REDUCER_NAME, reducer);

export {
	closeLiveCasinoFeedSocket,
	establishLiveCasinoFeedSocketConnection,
	fetchCarouselItems,
	fetchLiveCasinoData,
	fetchLiveCasinoGames,
	invalidateLiveCasinoData,
	setBannerAuth
};

export default reducer;
